'use client';

/**
 * HomepageV3 CompanyLogo — now consumes the published se3 design-system
 * package (`@startengine/design-system.se3.company-logo`, exported as
 * `Se3CompanyLogo`).
 *
 * The se3 component is props-driven and rewrites relative logo paths via a
 * `logoBasePath` prop. The original local component hardcoded the discover
 * public path (`/homepage-v3`, with logos under `/homepage-v3/logos`). This
 * thin wrapper injects that base path by default so call sites that pass
 * demoData logo paths (`assets/logos/x.svg`) resolve to
 * `/homepage-v3/logos/x.svg` exactly as before. Callers may still override
 * `logoBasePath`.
 *
 * Default `data-cy` (`company-logo`) and the three render forms
 * (banner / logo tile / monogram) are preserved by the package. The original
 * prop interface is re-declared here for type-safety (the package's generated
 * `.d.ts` uses loose `any` types).
 */
import type { FC, ReactNode } from 'react';
import {
  Se3CompanyLogo as Se3CompanyLogoRaw,
  resolveLogoSrc as se3ResolveLogoSrc,
} from '@startengine/design-system.se3.company-logo';
import type { Company } from 'components/HomepageV3/data/demoData';
import { withSe3AssetPrefix } from 'components/HomepageV3/assetPrefix';

// Prefixed with the deploy base path (`/discover` on AWS/ECS) so relative logo
// paths resolve under the served base path rather than the root domain.
const PUBLIC_LOGO_BASE = withSe3AssetPrefix('/homepage-v3/logos') as string;

export type LogoFit = 'cover' | 'contain';

export interface CompanyLogoProps {
  /** A full Company record (preferred), or pass the individual fields below. */
  company?: Pick<Company, 'logo' | 'mono' | 'name' | 'banner' | 'fit'> | null;
  logo?: string;
  mono?: string;
  name?: string;
  banner?: boolean;
  fit?: LogoFit;
  /** Rewrites relative logo paths; defaults to the discover public path. */
  logoBasePath?: string;
  /** Square size in px for the logo / monogram tile. Default 56. */
  size?: number;
  /** Corner radius in px. Default 14. */
  radius?: number;
  className?: string;
  children?: ReactNode;
  'data-cy'?: string;
}

const Se3CompanyLogo = Se3CompanyLogoRaw as unknown as FC<CompanyLogoProps>;

const ABSOLUTE_OR_DATA_RE = /^(?:[a-z][a-z\d+\-.]*:)?\/\//i;

export function shouldUseLogoBasePath(logo?: string): boolean {
  return Boolean(
    logo && !ABSOLUTE_OR_DATA_RE.test(logo) && !logo.startsWith('data:') && !logo.startsWith('/'),
  );
}

/** Rewrites a demoData logo path (`assets/logos/x.svg`) to the public path. */
export function resolveLogoSrc(logo?: string): string | undefined {
  if (!logo) return undefined;
  if (ABSOLUTE_OR_DATA_RE.test(logo) || logo.startsWith('data:')) return logo;
  if (logo.startsWith('/')) return withSe3AssetPrefix(logo);
  return se3ResolveLogoSrc(logo, PUBLIC_LOGO_BASE);
}

function CompanyLogo({ company, logo, logoBasePath, ...rest }: CompanyLogoProps) {
  const rawLogo = logo ?? company?.logo;
  const normalizedLogoBasePath = logoBasePath?.trim() || PUBLIC_LOGO_BASE;
  const effectiveLogoBasePath = shouldUseLogoBasePath(rawLogo) ? normalizedLogoBasePath : undefined;

  return (
    <Se3CompanyLogo company={company} logo={logo} logoBasePath={effectiveLogoBasePath} {...rest} />
  );
}

export default CompanyLogo;
